/**
 * Projects list page — loads from API, falls back to local state in dev mode.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserButton } from '@clerk/clerk-react';
import { Button, Card, CardContent } from '@frameit/ui';
import * as api from '../lib/api-client';
import type { ProjectListItem } from '@frameit/types';

// Check if Clerk is configured
const hasClerk = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const isDevMode = !hasClerk;

/** Projects page component */
export function ProjectsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load projects
  const loadProjects = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    if (isDevMode) {
      // Dev mode — show mock data
      setProjects([
        {
          id: 'prj_dev',
          name: 'Dev Project',
          artworkCount: 0,
          updatedAt: new Date().toISOString(),
        },
      ]);
      setIsLoading(false);
      return;
    }

    try {
      const result = await api.listProjects(1, 50);
      setProjects(result.items);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : 'Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Create new project
  const handleCreateProject = async (): Promise<void> => {
    if (isDevMode) {
      navigate('/editor');
      return;
    }

    try {
      const project = await api.createProject({ name: 'Untitled Project' });
      navigate(`/editor/${project.id}`);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : 'Failed to create project');
    }
  };

  // Delete project
  const handleDeleteProject = async (projectId: string, e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();

    if (isDevMode) return;

    try {
      await api.deleteProject(projectId);
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : 'Failed to delete project');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="h-14 border-b flex items-center justify-between px-4">
        <h1 className="font-semibold">FrameIt</h1>
        <div className="flex items-center gap-2">
          <Link to="/settings">
            <Button variant="ghost" size="sm">
              Settings
            </Button>
          </Link>
          {hasClerk ? (
            <UserButton afterSignOutUrl="/auth/sign-in" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
              D
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold">My Projects</h2>
          <Button onClick={handleCreateProject}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            New Project
          </Button>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
            {error}
            <button className="ml-2 underline hover:no-underline" onClick={loadProjects}>
              Retry
            </button>
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="aspect-[4/3] bg-muted rounded-t-lg" />
                <CardContent className="p-4">
                  <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Projects grid */}
        {!isLoading && projects.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project) => (
              <Link key={project.id} to={`/editor/${project.id}`}>
                <Card className="hover:border-primary/50 transition-colors cursor-pointer group relative">
                  <div className="aspect-[4/3] bg-muted rounded-t-lg flex items-center justify-center">
                    {project.thumbnailUrl ? (
                      <img
                        src={project.thumbnailUrl}
                        alt={project.name}
                        className="w-full h-full object-cover rounded-t-lg"
                      />
                    ) : (
                      <svg
                        className="w-12 h-12 text-muted-foreground"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-medium">{project.name}</h3>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">
                        {project.artworkCount} artwork{project.artworkCount !== 1 ? 's' : ''}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(project.updatedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </CardContent>

                  {/* Delete button */}
                  {!isDevMode && (
                    <button
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 hover:bg-destructive flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => handleDeleteProject(project.id, e)}
                    >
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  )}
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && projects.length === 0 && !error && (
          <div className="text-center py-12">
            <svg
              className="w-16 h-16 mx-auto text-muted-foreground mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            <h3 className="text-lg font-medium mb-2">No projects yet</h3>
            <p className="text-muted-foreground mb-4">Create your first project to get started</p>
            <Button onClick={handleCreateProject}>Create Project</Button>
          </div>
        )}
      </main>
    </div>
  );
}
